import { useMemo } from "react";
import { endOfWeek, startOfWeek } from "date-fns";

import { ExportV2 } from "@robin/common";

import { Patient } from "../models/patient";
import { PatientData } from "../models/patient-data";
import { usePatientData } from "./usePatientData";

export interface WeeklyTargetLogType extends ExportV2.WeeklyTargetLog {
  refTarget: ExportV2.WeeklyTarget;
}

export interface WeeklyTargetGoalType extends ExportV2.WeeklyTarget {
  totalCount: number;
  completedCount: number;
}

export interface WeeklyTargetTodoType
  extends Omit<ExportV2.WeeklyTargetLog, "timestamp"> {
  refTarget: ExportV2.WeeklyTarget;
}

export const useWeeklyTargetsData = ({
  exportId,
  patientId,
  targetId,
}: {
  exportId?: PatientData["exportId"];
  patientId: Patient["id"];
  targetId?: ExportV2.WeeklyTarget["id"] | null;
}): {
  goals: WeeklyTargetGoalType[];
  logs: WeeklyTargetLogType[];
  todo: WeeklyTargetTodoType[];
} => {
  const patientData = usePatientData({ patientId, exportId });

  const startOfWeekTimestamp = startOfWeek(new Date()).getTime();
  const endOfWeekTimestamp = endOfWeek(new Date()).getTime();

  return useMemo(() => {
    if (!patientData) return { goals: [], logs: [], todo: [] };
    const { weeklyTargetLogs, weeklyTargets } = patientData.data;

    // Goals’ processing
    const goals = weeklyTargets
      .sort((a, b) => Number(b.active) - Number(a.active))
      .map((goal) => {
        const completedCount = weeklyTargetLogs.filter(
          (log) => log.weeklyTargetId === goal.id
        ).length;

        const outstandingCount =
          goal.count -
          weeklyTargetLogs.filter(
            (l) =>
              l.weeklyTargetId === goal.id && l.timestamp > startOfWeekTimestamp
          ).length;

        const totalCount = outstandingCount + completedCount;

        return {
          ...goal,
          totalCount,
          completedCount,
        };
      });

    // Logs’ processing
    const outstandingLogs: WeeklyTargetTodoType[] = goals.flatMap((goal) => {
      const outstandingCount = goal.totalCount - goal.completedCount;
      return Array.from({ length: outstandingCount }, (_, i) => ({
        count: goal.count,
        id: 999999 + i,
        refTarget: goal,
        referenceTimestamp: endOfWeekTimestamp,
        weeklyTargetId: goal.id,
      }));
    });

    const logs: WeeklyTargetLogType[] = weeklyTargetLogs
      .filter((log) => !targetId || log.weeklyTargetId === targetId)
      .map((log) => ({
        ...log,
        refTarget: goals.find((goal) => goal.id === log.weeklyTargetId)!,
      }));

    const todo: WeeklyTargetTodoType[] = outstandingLogs.filter(
      (log) => !targetId || log.weeklyTargetId === targetId
    );

    return { goals, logs, todo };
  }, [patientData, endOfWeekTimestamp, startOfWeekTimestamp, targetId]);
};
